import React, { useCallback, useState } from "react";
import logo from "../../../assets/images/logo.svg";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import drawer from "../../../assets/images/icons/menu.svg";
import closeIcon from "../../../assets/images/icons/close.svg";
import useScrollPosition from "../../../shared/hooks/useScrollPosition";
import { sideMenu } from "../../../config/sideMenu";
import { showEnquireModal } from "../../../connected-components/enquireModal/reducer";
import { store } from "../../../redux/store";

/**
 * Header component for the application.
 * Renders the navigation bar and side menu toggle.
 */
const Header: React.FC = () => {
  const { t } = useTranslation();
  /**
   * State to manage the opening/closing of the side menu.
   */
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(false);
  const location = useLocation();
  const scroll = useScrollPosition();

  // Determine if on home page
  const isHome = location.pathname === "/";
  // Determine if scrolled (for home page)
  const isScrolled = scroll.y > 0;

  // Dynamic classes
  const headerBg = isHome && !isScrolled ? "bg-tertiary/25" : "bg-tertiary/25";
  const headerStyle = !isScrolled
    ? ""
    : isScrolled && !isSideMenuOpen
      ? "shadow-header"
      : "border-b border-gray-200";

  /**
   * Toggles the visibility of the side menu.
   */
  const toggleMenu = useCallback(() => {
    setIsSideMenuOpen((prev) => !prev);
  }, []);

  const handleEnquireNow = (closeMenu: boolean = true) => {
    if (closeMenu) {
      toggleMenu();
    }
    store.dispatch(showEnquireModal());
  };

  return (
    <header>
      <div
        className={`fixed top-0 left-0 w-full z-[12] transition-all duration-300 ${headerBg} ${headerStyle} h-[100px] lg:h-[100px]`}
      >
        <div className="h-full w-full max-w-[1440px] mx-auto px-4 lg:px-6">
          <div className="h-full flex items-center justify-between">
            {/* Left Menu Button */}
            <div className="w-[75px] lg:w-[200px] flex items-center">
              <button
                className="menu-toggle flex items-center"
                onClick={toggleMenu}
                aria-controls="primary-menu"
                aria-expanded={isSideMenuOpen}
              >
                <span className="block lg:hidden w-[28px] h-[20px]">
                  <span
                    className={`block w-full h-[3px] bg-primary mb-[6px] ${isSideMenuOpen ? "translate-y-[9px]" : ""} transition duration-300`}
                  ></span>
                  <span
                    className={`block w-full h-[3px] bg-primary mb-[6px] ${isSideMenuOpen ? "opacity-0" : ""} transition duration-300`}
                  ></span>
                  <span
                    className={`block w-full h-[3px] bg-primary ${isSideMenuOpen ? "translate-y-[-9px]" : ""} transition duration-300`}
                  ></span>
                </span>
                <span
                  className={`hidden lg:flex ${isSideMenuOpen ? "!hidden" : "block"} fs-sub-h2 text-primary hover:text-primary/80 text-xl transition-colors`}
                >
                  + Menu
                </span>
                <span
                  className={`hidden lg:${isSideMenuOpen ? "flex" : "hidden"} fs-sub-h2 text-primary/90 hover:text-primary text-xl transition-colors`}
                >
                  - Close
                </span>
              </button>
            </div>

            {/* Center Logo */}
            <div className="flex-1 flex justify-center items-center">
              <NavLink to="/" className="custom-logo-link">
                <img
                  src={logo}
                  alt="Logo"
                  className={`h-[80px] w-auto lg:h-[96px] transition-all duration-300`}
                />
              </NavLink>
            </div>

            {/* Right Enquire Button */}
            <div className="w-[75px] lg:w-[200px] flex items-center justify-end">
              <button
                onClick={() => handleEnquireNow(false)}
                className="hidden lg:block text-center fs-sub-h2 border transition-colors duration-300 bg-primary border-primary hover:border-primary/80 hover:bg-primary/80 text-white rounded-[10px] py-[8px] px-[28px]"
              >
                ENQUIRE NOW
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Header Background */}
      <div className="fixed top-0 left-0 w-full h-[100px] z-[10] pointer-events-none bg-white"></div>

      {/* Side Menu */}
      <div className="fixed top-0 left-0 w-full h-screen z-[11] pointer-events-none">
        <div
          className="fixed top-0 left-0 w-full h-screen z-[9] transition-all duration-500 ease-in-out overflow-auto bg-tertiary text-white pointer-events-auto"
          style={{
            opacity: isSideMenuOpen ? "1" : "0",
            visibility: isSideMenuOpen ? "visible" : "hidden",
            transition: "opacity 0.4s ease-in-out, visibility 0.4s ease-in-out",
            height: "100vh",
            minHeight: "-webkit-fill-available",
          }}
        >
          <div className="px-6 py-4 max-w-screen-xl mx-auto h-full flex flex-col pt-[100px]">
            <div className="space-y-8 py-4 flex-grow flex flex-col items-center justify-center">
              {sideMenu.map((item) => (
                <NavLink
                  key={item.title}
                  to={item.path}
                  onClick={toggleMenu}
                  className={({ isActive }) =>
                    `flex items-center justify-center py-4 text-4xl font-title italic font-bold transition-all ${
                      isActive
                        ? "text-primary underline decoration-primary"
                        : "text-primary/70 hover:text-primary hover:underline hover:decoration-primary"
                    }`
                  }
                >
                  {item.title.toUpperCase()}
                </NavLink>
              ))}
            </div>

            {/* Mobile Enquire Now button */}
            <div className="py-6 lg:hidden">
              <button
                onClick={() => handleEnquireNow(true)}
                className="block w-full text-center py-4 rounded-lg bg-white text-primary font-semibold text-base transition hover:bg-neutral-100"
              >
                ENQUIRE NOW
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
